/**
 * Caribou core-habitat area per region/year, from the MSPA rasters.
 *
 * Generated by caribou_tiling/code/caribou_stats_v3.py. Two areas are carried per
 * region-year, and the distinction matters:
 *
 *   coreHa     core habitat (raster classes 1-5)
 *   inRangeHa  the part of the FMU that lies inside a caribou range at all
 *
 * NoData in the source means "outside the caribou range", i.e. never assessed —
 * not "no habitat". So the honest denominator for a percentage is inRangeHa,
 * not the FMU's full area, which would silently count unassessed land as
 * habitat-free. getCoverage() below returns both so the UI can say how much of
 * the FMU was even evaluated.
 *
 *   { "troutlake": { "2024": { coreHa, inRangeHa, bins: {...}, ranges: [...] } } }
 *
 * Same loader shape as wildfireStats.js and clearcutAreaStats.js.
 */

let _statsPromise = null;

function loadCaribouStats() {
  if (!_statsPromise) {
    // Served from the app's own origin (public/data), not the tile CDN.
    const base = process.env.PUBLIC_URL || '';
    _statsPromise = fetch(`${base}/data/caribou_stats.json`)
      .then((r) => {
        if (!r.ok) throw new Error(`caribou_stats.json: HTTP ${r.status}`);
        return r.json();
      })
      .catch((err) => {
        console.error('[CaribouStats]', err);
        _statsPromise = null;
        throw err;
      });
  }
  return _statsPromise;
}

/**
 * The map ramp: viridis across all 19 core-patch size classes —
 * purple/blue = smallest patches, green = medium, yellow = largest.
 *
 * Enough stops to reproduce viridis's curve in a CSS gradient; the tiles
 * themselves use the exact 19 sampled colours (VIRIDIS_19 in
 * caribou_tiling/code/caribou_tiler.py).
 *
 * Tiles are painted at generation time, so changing a colour here alone makes
 * the legend lie about the map — the tiler has to change too, and the pyramid
 * has to be remapped (see recolour_tiles.py).
 */
export const RAMP_STOPS = [
  // First stop carries the same alpha the tiles use for class 1, so the legend
  // bar cannot imply a solidity the map does not draw.
  'rgba(68,1,84,0.27)', '#3b528b', '#21918c', '#5ec962', '#fde725',
];

/**
 * Tick positions along that ramp, as percentages. With 5 classes each sits at
 * (n - 1) / 4, so every class is labelled -- no need to pick round-area ones
 * the way the 19-class ramp did.
 */
export const RAMP_TICKS = [
  { pct: 0, label: '<100 ha' },
  { pct: 25, label: '100-500' },
  { pct: 50, label: '500-1k' },
  { pct: 75, label: '1k-10k' },
  { pct: 100, label: '>10k ha' },
];

/**
 * Coarse groupings of the same 19 classes, used only for the share-of-habitat
 * numbers — the map itself is the full ramp above, not these four steps.
 */
/**
 * The class excluded from the "functional" series: patches under 100 ha.
 * Must match SIZE_BINS[0].key.
 */
export const SUB_FUNCTIONAL_KEY = 'subFunctional';

export const SIZE_BINS = [
  // alpha mirrors ALPHA_BY_CLASS in caribou_tiling/code/caribou_tiler_v3.py, so
  // a swatch shows the class at the strength it actually renders on the map.
  { key: 'subFunctional', label: '<100 ha', color: '#440154', alpha: 70 },
  { key: 'small', label: '100-500 ha', color: '#3b528b', alpha: 200 },
  { key: 'moderate', label: '500-1k ha', color: '#21918c', alpha: 200 },
  { key: 'large', label: '1k-10k ha', color: '#5ec962', alpha: 200 },
  { key: 'veryLarge', label: '>10k ha', color: '#fde725', alpha: 200 },
];

/**
 * Core habitat per year, summed across the given regions.
 * Every year in `years` gets an entry so the timeline stays continuous.
 *
 * @param {string[]} regions - FMU ids
 * @param {number[]} years   - years to report on
 * @returns {Promise<Array<{year:number, coreHa:number, inRangeHa:number, pct:number|null}>>}
 */
export async function getCoreHabitatPerYear(regions, years) {
  if (!Array.isArray(regions) || regions.length === 0) return [];

  const all = await loadCaribouStats();

  return years.map((year) => {
    let coreHa = 0;
    let inRangeHa = 0;
    let subFunctionalHa = 0;

    for (const region of regions) {
      const entry = all[String(region || '').toLowerCase()]?.[String(year)];
      if (entry) {
        coreHa += entry.coreHa || 0;
        inRangeHa += entry.inRangeHa || 0;
        subFunctionalHa += entry.bins?.[SUB_FUNCTIONAL_KEY] || 0;
      }
    }

    // Core in patches >= 100 ha. Class 1 is the 3x3-erosion speckle: real
    // pixels, but fragments that size do not support a herd, and in some FMUs
    // they are two thirds of the "core" total (Nipigon 71%, Berens 67%), which
    // makes the headline area move for reasons that are not about habitat.
    const functionalHa = Math.max(0, coreHa - subFunctionalHa);

    return {
      year,
      coreHa: Math.round(coreHa * 10) / 10,
      functionalHa: Math.round(functionalHa * 10) / 10,
      subFunctionalHa: Math.round(subFunctionalHa * 10) / 10,
      inRangeHa: Math.round(inRangeHa * 10) / 10,
      // null rather than 0 when nothing was assessed, so the UI can say
      // "not in caribou range" instead of showing a misleading 0%.
      pct: inRangeHa > 0 ? (coreHa / inRangeHa) * 100 : null,
      functionalPct: inRangeHa > 0 ? (functionalHa / inRangeHa) * 100 : null,
    };
  });
}

/**
 * Size-class breakdown for one year, summed across regions.
 * @returns {Promise<Array<{key:string, label:string, color:string, areaHa:number}>>}
 */
export async function getSizeBreakdown(regions, year) {
  if (!Array.isArray(regions) || regions.length === 0) return [];

  const all = await loadCaribouStats();
  const totals = Object.fromEntries(SIZE_BINS.map((b) => [b.key, 0]));

  for (const region of regions) {
    const bins = all[String(region || '').toLowerCase()]?.[String(year)]?.bins;
    if (!bins) continue;
    for (const bin of SIZE_BINS) {
      totals[bin.key] += bins[bin.key] || 0;
    }
  }

  return SIZE_BINS.map((b) => ({ ...b, areaHa: Math.round(totals[b.key] * 10) / 10 }));
}

/**
 * The seven modelled ranges, in the order the panel lists them.
 *
 * `id` is the lowercase key the stats use. Both colour steps are read from here
 * — the map outline and the panel swatch, so the two cannot drift. The `color`
 * property in public/data/caribou_ranges.geojson is deliberately ignored; these
 * override it, so make_range_geojson.py need not be re-run to restyle.
 *
 * No red and no green: on a habitat map those read as bad and good, and a
 * population range is neither. The remaining hues are ordered for colour-vision
 * separation rather than appearance — validated as a set, worst adjacent pair
 * CVD ΔE 16.3 light / 13.2 dark and normal-vision 19.6 / 19.3 (OKLab ×100,
 * ≥8 and ≥15 the respective gates). Re-running that check is the only safe way
 * to change one: swapping a hue by eye can quietly collapse a pair.
 *
 * `color` is used for the map, where marks sit on the basemap rather than the
 * app surface; `colorDark` is the step for the dark panel, validated against it.
 * Magenta and yellow fall under 3:1 on white, so every swatch carries its name
 * beside it and the map outline carries a tooltip — identity is never colour
 * alone.
 */
// No warm hues. Orange, amber and rust read as alarm on an ecological map -- a
// population range is not a warning -- which is the same reason red and green
// were dropped from the habitat ramp. Green survives here because it carries no
// polarity on its own, only in opposition to red.
//
// Validated with the dataviz palette checker rather than picked by eye:
//   light  worst adjacent CVD dE 7.6 (deutan), normal-vision 16.9
//   dark   worst adjacent CVD dE 6.7 (deutan), normal-vision 17.0
// against gates of 8 (target) / 15 (hard floor). Both sit in the 6-8 CVD band,
// which is legal ONLY with secondary encoding -- every swatch carries its name
// and every map outline a tooltip, so identity is never colour alone.
//
// Known trade-off: sydney's dark step is L 0.71 against a 0.48-0.67 band. Seven
// hues will not fit the dark band's narrower range without the warm arc, and
// darkening it further collapses its separation from pagwachuan's cyan under
// protanopia. Accepted for a 2px outline; revisit if these ever carry fills.
export const CARIBOU_RANGES = [
  { id: 'berens', label: 'Berens', color: '#2a78d6', colorDark: '#3d7fd6' },
  { id: 'brightsand', label: 'Brightsand', color: '#3f9147', colorDark: '#3f8f4a' },
  { id: 'churchill', label: 'Churchill', color: '#b8479e', colorDark: '#b8479e' },
  { id: 'kesagami', label: 'Kesagami', color: '#2bb391', colorDark: '#1f9678' },
  { id: 'nipigon', label: 'Nipigon', color: '#8461d6', colorDark: '#8461d6' },
  { id: 'pagwachuan', label: 'Pagwachuan', color: '#0089ad', colorDark: '#0f83a3' },
  { id: 'sydney', label: 'Sydney', color: '#e88fb0', colorDark: '#d484a6' },
];

/** Map outline colour for a range, by RANGE_NAME or id. */
export const rangeColor = (name) => CARIBOU_RANGES.find(
  (r) => r.id === String(name || '').toLowerCase(),
)?.color;

/**
 * FMUs whose tiles carry any of the given ranges.
 *
 * The habitat raster is already clipped to the ranges (NoData outside), so the
 * union of these FMUs' tiles draws the range and nothing beyond it — no
 * range-keyed pyramid needed. Ranges share FMUs, so the union of two ranges is
 * smaller than the sum.
 *
 * Membership is read across every year rather than one, so a range does not
 * lose an FMU on a year where that FMU happens to have no assessed area.
 *
 * @param {string[]} ranges - range ids
 * @returns {Promise<string[]>} FMU ids
 */
export async function getFmusForRanges(ranges) {
  if (!Array.isArray(ranges) || ranges.length === 0) return [];

  const all = await loadCaribouStats();
  const wanted = new Set(ranges.map((r) => String(r || '').toLowerCase()));

  return Object.entries(all)
    .filter(([, years]) => Object.values(years).some(
      (entry) => (entry?.ranges || []).some((r) => wanted.has(String(r).toLowerCase())),
    ))
    .map(([fmu]) => fmu)
    .sort();
}

/** Which caribou ranges cover the given regions — shown as provenance. */
export async function getRangeNames(regions, year) {
  if (!Array.isArray(regions) || regions.length === 0) return [];

  const all = await loadCaribouStats();
  const names = new Set();

  for (const region of regions) {
    const entry = all[String(region || '').toLowerCase()]?.[String(year)];
    (entry?.ranges || []).forEach((r) => names.add(r));
  }

  return [...names].sort();
}

export function clearCaribouStatsCache() {
  _statsPromise = null;
}
